/* eslint-disable react/no-unknown-property */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import { useSortable } from '@dnd-kit/react/sortable';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { directionBiased } from '@dnd-kit/collision';
import {
  Dropdown,
  Icon,
  Menu,
  MenuDivider,
  MenuItem,
} from '@pypestream/design-system';

import { NodeColumns, tableStore } from '../../../stores/table-store';
import './draggable-row.scss';

interface DraggableRowProps {
  rowId: string;
  children: React.ReactNode;
  hasError?: boolean;
  onResizeStart: (rowId: string) => void;
  isResizing: boolean;
  onResetSize: (rowId: string) => void;
  currentResizeHeight: number | null;
}

const DragHandle = ({ index }: { index: number }) => {
  return (
    <div
      className="c-editor-table-row--drag-handle"
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        test-id={`c-editor-table--indexCell-${index}-drag-icon`}
        className="c-editor-table-row--drag-icon"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      >
        <path
          stroke="none"
          fill="currentColor"
          d="M15 15.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM15 9.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM15 8.016q-0.797 0-1.406-0.609t-0.609-1.406 0.609-1.406 1.406-0.609 1.406 0.609 0.609 1.406-0.609 1.406-1.406 0.609zM9 3.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM9 9.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM11.016 18q0 0.797-0.609 1.406t-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609 1.406 0.609 0.609 1.406z"
        ></path>
      </svg>
    </div>
  );
};

const DraggableRowComponent = (
  {
    rowId,
    children,
    hasError,
    onResizeStart,
    isResizing,
    onResetSize,
    currentResizeHeight,
  }: DraggableRowProps,
  ref: React.Ref<HTMLTableRowElement>
) => {
  const tableCellRef = useRef<HTMLTableCellElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  const rowIndex = tableStore.getSortedRowIndex(rowId);

  const isRowCopiedOrCut = tableStore.isRowCopiedOrCut(rowId);
  const isRowSelected = tableStore.isSelectedRow(rowId);

  // Get row height from store, or use currentResizeHeight if actively resizing
  const storedRowHeight = tableStore.getValue(
    rowId,
    NodeColumns.settings
  )?.rowHeight;
  const rowHeight =
    isResizing && currentResizeHeight !== null
      ? currentResizeHeight
      : storedRowHeight;

  const { dragSession } = tableStore;
  const isActiveRow = dragSession?.activeRowId === rowId;
  const isMultiDrag = dragSession?.isMulti;

  const isPartOfMultiSelection =
    isRowSelected &&
    tableStore.selectedRows.size > 1 &&
    tableStore.isSelectionContinuous;

  const shouldDisableSortable =
    isMultiDrag && dragSession?.activeRowId !== rowId;

  const { ref: sortableRef, isDragging } = useSortable({
    id: rowId,
    index: rowIndex - tableStore.currentPage * tableStore.paginationSize,
    handle: dragHandleRef,
    modifiers: [RestrictToVerticalAxis],
    collisionDetector: directionBiased,
    disabled: shouldDisableSortable,
    transition: isMultiDrag ? null : undefined,
  });

  const rowElementRef = useRef<HTMLTableRowElement | null>(null);

  const rowSortableRef =
    isMultiDrag || isPartOfMultiSelection ? undefined : sortableRef;

  const rowIntent = tableStore.getValue(rowId, NodeColumns.intent);

  const isRowHighlighted = tableStore.doesRowContainSelectedCells(rowId);

  const [dragOffsetY, setDragOffsetY] = useState(0);
  const dragStartY = useRef<number | null>(null);
  const scrollAnimationRef = useRef<number | null>(null);

  // const isMouseDown = useRef('closing');

  // const [currentHeight, setCurrentHeight] = useState<string | number>(
  //   'inherit'
  // );

  /**
   * manually sets the current height of the row being dragged, resets otherwise. this is a workaround to a newer dndkit bug where taller row heights are not accurately being calculated, especially if the UI underneath changes height after the initial rendering (ex. the Select component when multiple values are selected) */
  // @todo: re-enable after further troubleshooting
  // useEffect(() => {
  //   // if (
  //   //   !isDragging &&
  //   //   !isDropping &&
  //   //   currentHeight !== 'inherit' &&
  //   //   isMouseDown.current === false
  //   // ) {
  //   //   setTimeout(() => {
  //   //     setCurrentHeight('inherit');
  //   //   }, 500);
  //   // }
  //   // else
  //   if (
  //     currentHeight === 'inherit' ||
  //     (tableCellRef.current &&
  //       currentHeight !== `${tableCellRef.current.offsetHeight}px`)
  //   ) {
  //     // setTimeout(() => {
  //     // setCurrentHeight('inherit');

  //     if (
  //       tableCellRef.current?.offsetHeight &&
  //       tableCellRef.current.offsetHeight + 'px' !== currentHeight
  //     ) {
  //       setCurrentHeight(`${tableCellRef.current.offsetHeight}px`);
  //     }
  //     // }, 2000);
  //   }
  // }, [currentHeight]);

  const DRAG_SCROLL_EDGE_THRESHOLD = 60;
  const DRAG_SCROLL_SPEED = 8;

  const isOversizeDrag = dragSession?.isOversizeDrag ?? false;
  const isMultiDragActive = isActiveRow && isMultiDrag;

  useEffect(() => {
    if (!isMultiDragActive || !isOversizeDrag) return;

    const scrollContainer = document.querySelector('.c-editor-table-scroller');
    if (!scrollContainer) return;

    const preventScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    scrollContainer.addEventListener('wheel', preventScroll, {
      passive: false,
    });
    scrollContainer.addEventListener('touchmove', preventScroll, {
      passive: false,
    });

    return () => {
      scrollContainer.removeEventListener('wheel', preventScroll);
      scrollContainer.removeEventListener('touchmove', preventScroll);
    };
  }, [isMultiDragActive, isOversizeDrag]);

  useEffect(() => {
    if (!isMultiDragActive) {
      setDragOffsetY(0);
      dragStartY.current = null;
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
      return;
    }

    const scrollContainer = document.querySelector('.c-editor-table-scroller');
    let lastPointerY = 0;

    const autoScroll = () => {
      if (!scrollContainer || !isMultiDragActive || isOversizeDrag) return;

      const rect = scrollContainer.getBoundingClientRect();
      const distanceFromTop = lastPointerY - rect.top;
      const distanceFromBottom = rect.bottom - lastPointerY;

      let scrollDelta = 0;
      if (
        distanceFromTop < DRAG_SCROLL_EDGE_THRESHOLD &&
        scrollContainer.scrollTop > 0
      ) {
        scrollDelta =
          -DRAG_SCROLL_SPEED *
          (1 - distanceFromTop / DRAG_SCROLL_EDGE_THRESHOLD);
      } else if (
        distanceFromBottom < DRAG_SCROLL_EDGE_THRESHOLD &&
        scrollContainer.scrollTop <
          scrollContainer.scrollHeight - scrollContainer.clientHeight
      ) {
        scrollDelta =
          DRAG_SCROLL_SPEED *
          (1 - distanceFromBottom / DRAG_SCROLL_EDGE_THRESHOLD);
      }

      if (scrollDelta !== 0) {
        scrollContainer.scrollTop += scrollDelta;
      }

      scrollAnimationRef.current = requestAnimationFrame(autoScroll);
    };

    const handlePointerMove = (e: PointerEvent) => {
      lastPointerY = e.clientY;

      if (dragStartY.current === null) {
        dragStartY.current = e.clientY;

        if (!isOversizeDrag) {
          scrollAnimationRef.current = requestAnimationFrame(autoScroll);
        }
      }

      if (!isOversizeDrag) {
        setDragOffsetY(e.clientY - dragStartY.current);
      }
    };

    document.addEventListener('pointermove', handlePointerMove);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
    };
  }, [isMultiDragActive, isOversizeDrag]);

  const [contextMenuState, setContextMenuState] = useState<
    'closed' | 'closing' | 'open'
  >('closed');

  const resetSize = () => {
    onResetSize(rowId);
  };

  // Modify children to add drag handle to first cell
  const childrenWithHandles = React.Children.map(children, (child) => {
    return child;
  })?.slice(1);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (tableStore.canDeleteRow(rowId)) {
      setContextMenuState('closing');

      setTimeout(() => {
        tableStore.deleteRows([...tableStore.selectedRows.values()]);
      }, 50);
    }
  };

  const handleIndexCellClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    tableStore.deselectAllCells();
    tableStore.isCellBeingEdited = false;

    if (contextMenuState === 'open') {
      setContextMenuState('closing');
    }

    if (tableCellRef.current) {
      tableCellRef.current.removeAttribute('data-popover-open');
    }

    if (e.shiftKey) {
      tableStore.selectRowRange(rowIndex);
      (e.currentTarget as HTMLElement).blur();
      return;
    }

    if (
      tableStore.rowRangeSelection?.isSelecting ||
      tableStore.consumeSuppressNextRowClick()
    ) {
      return;
    }

    if (tableStore.isSelectedRow(rowId)) {
      if (!e.metaKey && !e.ctrlKey) {
        tableStore.deselectAllRows();
        tableStore.selectRow(rowId);
        tableStore.setRowSelectionAnchor(rowIndex);
      } else {
        tableStore.deSelectRow(rowId);
      }
    } else {
      if (e.metaKey || e.ctrlKey) {
        tableStore.selectRow(rowId);
        tableStore.setRowSelectionAnchor(rowIndex);
      } else {
        if (tableStore.selectedRows.size > 0) {
          tableStore.deselectAllRows();
        }
        tableStore.selectRow(rowId);
        tableStore.setRowSelectionAnchor(rowIndex);
      }
    }
  };

  const DRAG_THRESHOLD = 2;
  const SCROLL_EDGE_SIZE = 50;
  const SCROLL_SPEED = 10;

  const handleIndexCellPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;

    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    if (isRowSelected) return;

    tableStore.suppressNextRowClick = false;

    const target = e.currentTarget as HTMLElement;
    const { pointerId, clientX: startX, clientY: startY } = e;
    const startRowIndex = rowIndex;
    let rangeStarted = false;
    let hasPointerCapture = false;
    let scrollAnimationId: number | null = null;
    let lastPointerY = startY;

    const scrollContainer = target.closest('.c-editor-table-scroller');

    const autoScroll = () => {
      if (!scrollContainer || !rangeStarted) return;

      const rect = scrollContainer.getBoundingClientRect();
      const distanceFromTop = lastPointerY - rect.top;
      const distanceFromBottom = rect.bottom - lastPointerY;

      let scrollDelta = 0;
      if (distanceFromTop < SCROLL_EDGE_SIZE && scrollContainer.scrollTop > 0) {
        scrollDelta = -SCROLL_SPEED * (1 - distanceFromTop / SCROLL_EDGE_SIZE);
      } else if (
        distanceFromBottom < SCROLL_EDGE_SIZE &&
        scrollContainer.scrollTop <
          scrollContainer.scrollHeight - scrollContainer.clientHeight
      ) {
        scrollDelta =
          SCROLL_SPEED * (1 - distanceFromBottom / SCROLL_EDGE_SIZE);
      }

      if (scrollDelta !== 0) {
        scrollContainer.scrollTop += scrollDelta;

        const elements = document.elementsFromPoint(
          startX + (lastPointerY - startY) * 0.1,
          lastPointerY
        );
        for (const element of elements) {
          const row = element.closest('tr[data-row-index]');
          if (row) {
            const rowIndexAttr = row.getAttribute('data-row-index');
            if (rowIndexAttr !== null) {
              const idx = parseInt(rowIndexAttr, 10);
              if (!isNaN(idx)) {
                tableStore.updateRowRangeSelection(idx);
                break;
              }
            }
          }
        }
      }

      scrollAnimationId = requestAnimationFrame(autoScroll);
    };

    const handleMove = (moveEvent: PointerEvent) => {
      if (tableStore.dragSession) {
        cleanup();
        return;
      }

      lastPointerY = moveEvent.clientY;
      const dx = Math.abs(moveEvent.clientX - startX);
      const dy = Math.abs(moveEvent.clientY - startY);

      if (!rangeStarted && (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD)) {
        rangeStarted = true;

        if (!target.hasPointerCapture(pointerId)) {
          target.setPointerCapture(pointerId);
          hasPointerCapture = true;
        }

        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        tableStore.startRowRangeSelection(startRowIndex);

        scrollAnimationId = requestAnimationFrame(autoScroll);
      }

      if (rangeStarted && tableStore.rowRangeSelection?.isSelecting) {
        moveEvent.preventDefault();
        moveEvent.stopPropagation();

        const elements = document.elementsFromPoint(
          moveEvent.clientX,
          moveEvent.clientY
        );
        for (const element of elements) {
          const row = element.closest('tr[data-row-index]');
          if (row) {
            const rowIndexAttr = row.getAttribute('data-row-index');
            if (rowIndexAttr !== null) {
              const idx = parseInt(rowIndexAttr, 10);
              if (!isNaN(idx)) {
                tableStore.updateRowRangeSelection(idx);
                break;
              }
            }
          }
        }
      }
    };

    const handleUp = () => {
      if (rangeStarted && tableStore.rowRangeSelection?.isSelecting) {
        tableStore.endRowRangeSelection();
      }
      cleanup();
    };

    const cleanup = () => {
      if (scrollAnimationId !== null) {
        cancelAnimationFrame(scrollAnimationId);
        scrollAnimationId = null;
      }
      if (hasPointerCapture && target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture(pointerId);
        hasPointerCapture = false;
      }
      target.blur();
      document.removeEventListener('pointermove', handleMove, {
        capture: true,
      });
      document.removeEventListener('pointerup', handleUp, { capture: true });
      document.removeEventListener('pointercancel', handleUp, {
        capture: true,
      });
    };

    document.addEventListener('pointermove', handleMove, { capture: true });
    document.addEventListener('pointerup', handleUp, { capture: true });
    document.addEventListener('pointercancel', handleUp, { capture: true });
  };

  const cellId = `editor-table-td-index-${rowId}`;

  useEffect(() => {
    if (contextMenuState !== 'closing') return;

    if (contextMenuState === 'closing') {
      setTimeout(() => {
        setContextMenuState('closed');

        if (tableCellRef.current) {
          tableCellRef.current.removeAttribute('data-popover-open');
        }

        // tableStore.deselectAllRows();
      }, 50);
    }
  }, [contextMenuState]);

  const dropdown = (
    <Dropdown
      trigger={cellId}
      test-id={`c-editor-table--indexCell-td-${rowIndex}-dropdown`}
      id="row-context-popover"
      active={contextMenuState === 'open'}
      disableTriggerListeners
      onclose={() => {
        // tableStore.deselectAllRows();
        setContextMenuState('closing');

        if (tableCellRef.current) {
          tableCellRef.current.removeAttribute('data-popover-open');
        }
      }}
    >
      <Menu>
        <MenuItem
          test-id="c-editor-table-row-context-menu-copy"
          onClick={(e) => {
            e.preventDefault();
            tableStore.copyRows([...tableStore.selectedRows.values()]);
            setContextMenuState('closing');
          }}
        >
          <Icon size="small" name="copy" slot="prefix" />
          Copy
        </MenuItem>

        <MenuItem
          test-id="c-editor-table-row-context-menu-cut"
          onClick={(e) => {
            e.preventDefault();
            tableStore.cutRows([...tableStore.selectedRows.values()]);
            setContextMenuState('closing');
          }}
        >
          <Icon size="small" name="scissors" slot="prefix" />
          Cut
        </MenuItem>

        <MenuItem
          test-id="c-editor-table-row-context-menu-paste-above"
          onClick={(e) => {
            e.preventDefault();
            tableStore.pasteRow(rowId, 'above');
            setContextMenuState('closing');
          }}
          disabled={!tableStore.canPasteRow()}
        >
          <Icon size="small" name="paste-above" slot="prefix" />
          Paste above
        </MenuItem>
        <MenuItem
          test-id="c-editor-table-row-context-menu-paste-replace"
          onClick={(e) => {
            e.preventDefault();
            tableStore.pasteRow(rowId, 'replace');
            setContextMenuState('closing');
          }}
          disabled={!tableStore.canPasteRow()}
        >
          <Icon size="small" name="clipboard" slot="prefix" />
          Paste
        </MenuItem>

        <MenuItem
          test-id="c-editor-table-row-context-menu-paste-below"
          onClick={(e) => {
            e.preventDefault();
            tableStore.pasteRow(rowId, 'below');
            setContextMenuState('closing');
          }}
          disabled={!tableStore.canPasteRow()}
        >
          <Icon size="small" name="paste-below" slot="prefix" />
          Paste below
        </MenuItem>
        <MenuDivider />
        <MenuItem
          test-id="c-editor-table-row-context-menu-insert-above"
          onClick={(e) => {
            e.preventDefault();
            tableStore.insertRowAbove(rowId);
            setContextMenuState('closing');
          }}
        >
          <Icon size="small" name="arrow-up" slot="prefix" />
          Insert 1 node above
        </MenuItem>
        <MenuItem
          test-id="c-editor-table-row-context-menu-insert-below"
          onClick={(e) => {
            e.preventDefault();
            tableStore.insertRowBelow(rowId);
            setContextMenuState('closing');
          }}
        >
          <Icon size="small" name="arrow-down" slot="prefix" />
          Insert 1 node below
        </MenuItem>
        <MenuItem
          test-id="c-editor-table-row-context-menu-duplicate"
          onClick={(e) => {
            e.preventDefault();
            tableStore.duplicateRow(rowId);
            setContextMenuState('closing');
          }}
        >
          <Icon size="small" name="duplicate" slot="prefix" />
          Duplicate 1 node
        </MenuItem>
        {/* <MenuItem disabled>
            <Icon size="small" name="switch" slot="prefix" />
            Replace 1 node
          </MenuItem> */}
        <MenuItem
          test-id="c-editor-table-row-context-menu-nl-entry"
          onClick={(e) => {
            e.preventDefault();
            setContextMenuState('closing');
            tableStore.toggleOutOfScopeIntent(rowId);
          }}
        >
          <Icon size="small" name="stars" slot="prefix" />
          {rowIntent === 'out_of_scope'
            ? 'Clear as NL Entry node'
            : 'Set as NL entry node'}
        </MenuItem>
        <MenuDivider />
        {/* <MenuItem disabled>
            <Icon size="small" name="unlink" slot="prefix" />
            Unlink node
          </MenuItem> */}
        <MenuItem
          onClick={handleDelete}
          disabled={!tableStore.canDeleteRow(rowId)}
          title={tableStore.canDeleteRow(rowId) ? '' : 'Cannot delete last row'}
          test-id="c-editor-table-row-context-menu-delete"
        >
          <Icon size="small" name="delete" slot="prefix" />
          Delete
        </MenuItem>
        <MenuItem
          test-id="c-editor-table-row-context-menu-clear"
          onClick={(e) => {
            e.preventDefault();
            tableStore.clearRows([...tableStore.selectedRows.values()]);
            setContextMenuState('closing');
          }}
        >
          <Icon size="small" name="eraser" slot="prefix" />
          Clear
        </MenuItem>
      </Menu>
    </Dropdown>
  );

  const renderIndexCell = () => {
    return (
      <td
        test-id={`c-editor-table--indexCell-td-${rowIndex}`}
        id={cellId}
        className={`c-editor-table-td c-editor-table-td--index-cell ${hasError ? 'c-editor-table-td--error' : ''} ${rowIntent === 'out_of_scope' ? 'c-editor-table-td--out-of-scope' : ''} ${isRowHighlighted ? 'c-editor-table-td--highlighted' : ''}`}
        style={{
          padding: tableStore.isEditing() ? '0' : '6px 12px',
          textAlign: 'center',
          position: 'sticky',
          pointerEvents: isResizing ? 'none' : 'auto', // Disable pointer events during resize
        }}
        role="gridcell"
        tabIndex={-1}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          tableStore.deselectAllCells();
          if (tableStore.selectedRows.size >= 1) {
            if (!tableStore.isSelectedRow(rowId)) {
              tableStore.deselectAllRows();
            }
          }
          tableStore.selectRow(rowId);

          e.currentTarget.setAttribute('data-popover-open', '');
          setContextMenuState('open');
        }}
      >
        <div
          test-id={`c-editor-table--indexCell-${rowIndex}-${tableStore.isEditing() ? 'edit' : 'view'}-mode-inner`}
          className="c-editor-cell__inner"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            height: '100%',
            zIndex: 11,
          }}
          ref={tableCellRef}
        >
          {tableStore.isEditing() ? (
            <div
              onClick={handleIndexCellClick}
              ref={dragHandleRef}
              className="c-editor-table-tr__drag-hadle"
              onPointerDown={handleIndexCellPointerDown}
              test-id={`c-editor-table--indexCell-${rowIndex}-drag-handle`}
            >
              <div
                className="c-editor-table-row--drag-handle"
                style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  test-id={`c-editor-table--indexCell-${rowIndex}-drag-icon`}
                  className="c-editor-table-row--drag-icon"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                >
                  <path
                    stroke="none"
                    fill="currentColor"
                    d="M15 15.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM15 9.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM15 8.016q-0.797 0-1.406-0.609t-0.609-1.406 0.609-1.406 1.406-0.609 1.406 0.609 0.609 1.406-0.609 1.406-1.406 0.609zM9 3.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM9 9.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM11.016 18q0 0.797-0.609 1.406t-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609 1.406 0.609 0.609 1.406z"
                  ></path>
                </svg>
              </div>
              <span className="c-editor-table-row--row-number">
                {rowIndex + 1}
              </span>
            </div>
          ) : (
            <span className="c-editor-table-row--row-number">
              {rowIndex + 1}
            </span>
          )}
        </div>
        {/* {(child as React.ReactElement).props.children} */}
        {/* Drag proxy: invisible element for multi-row drag */}
        <div
          ref={isPartOfMultiSelection ? sortableRef : undefined}
          className="c-editor-table-tr__drag-proxy"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            opacity: 0,
          }}
        />
        {/* Resize handle at bottom */}
        <div
          ref={resizeHandleRef}
          className="c-editor-table-row-resize-handle"
          onDoubleClick={resetSize}
          onMouseDown={() => {
            onResizeStart(rowId);
          }}
        />
        {tableStore.isEditing() && contextMenuState !== 'closed' && (
          <>{dropdown}</>
        )}
      </td>
    );
  };

  const rowStyles: React.CSSProperties = {};
  if (rowHeight) {
    (rowStyles as any)['--c-editor-table-tr-height'] = `${rowHeight}px`;
  }

  const insertionIndex = isMultiDrag ? dragSession?.insertionIndex : null;
  const showInsertionAbove = insertionIndex === rowIndex;
  const visibleRowCount = tableStore
    .getAllSortedRows()
    .filter((row) => row.hidden !== true).length;
  const showInsertionBelow =
    insertionIndex === rowIndex + 1 && rowIndex === visibleRowCount - 1;

  const classNames = [
    'c-editor-table-tr',
    isDragging && !isMultiDrag && 'c-editor-table-tr--dragging',
    isRowSelected && 'c-editor-table-tr--selected',
    isRowCopiedOrCut && 'c-editor-table-tr--copied',
    hasError && 'c-editor-table-tr--error',
    isResizing && 'c-editor-table-tr--resizing',
    isMultiDrag && 'c-editor-table-tr--drag-frozen',
  ]
    .filter(Boolean)
    .join(' ');

  const shouldRenderShadow =
    isMultiDragActive && dragSession && dragSession.groupHeight > 0;

  const shadowHeight = shouldRenderShadow ? dragSession.groupHeight : 0;

  let shadowTop = 0;
  let shadowLeft = 0;
  let shadowWidth = 0;
  let shadowPosition: 'fixed' | 'absolute' = 'fixed';

  if (shouldRenderShadow && dragSession) {
    if (isOversizeDrag) {
      shadowPosition = 'absolute';
      const scrollContainer = document.querySelector(
        '.c-editor-table-scroller'
      );
      if (scrollContainer && rowElementRef.current) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const firstSelectedId = dragSession.selectedRowIds[0];
        const firstRowEl = firstSelectedId
          ? document.querySelector(`[data-rowid="${firstSelectedId}"]`)
          : null;
        if (firstRowEl) {
          const rowRect = firstRowEl.getBoundingClientRect();
          shadowTop =
            rowRect.top - containerRect.top + scrollContainer.scrollTop;
          shadowLeft = rowRect.left - containerRect.left;
          shadowWidth = rowRect.width;
        }
      }
    } else {
      shadowTop = dragSession.initialTop + dragOffsetY;
      shadowLeft = dragSession.initialLeft;
      shadowWidth = dragSession.initialWidth;
      const viewportHeight = window.innerHeight;
      const minTop = 10;
      const maxTop = viewportHeight - shadowHeight - 10;
      shadowTop = Math.max(minTop, Math.min(shadowTop, maxTop));
    }
  }

  const scrollContainer = document.querySelector('.c-editor-table-scroller');
  const shadowPortal = shouldRenderShadow
    ? createPortal(
        <div
          className="c-editor-table-tr__drag-shadow"
          style={{
            position: shadowPosition,
            top: shadowTop,
            left: isOversizeDrag ? shadowLeft : dragSession.initialLeft,
            width: isOversizeDrag ? shadowWidth : dragSession.initialWidth,
            height: shadowHeight,
            backgroundColor: 'rgba(139, 92, 246, 0.15)',
            border: '2px solid var(--ps-colors-purple-500, #8b5cf6)',
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        />,
        isOversizeDrag && scrollContainer ? scrollContainer : document.body
      )
    : null;

  const combinedRef = (element: HTMLTableRowElement | null) => {
    rowElementRef.current = element;

    if (rowSortableRef) {
      rowSortableRef(element);
    }

    if (ref) {
      if (typeof ref === 'function') {
        ref(element);
      } else {
        (ref as React.MutableRefObject<HTMLTableRowElement | null>).current =
          element;
      }
    }
  };

  const insertionLineStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    height: '3px',
    backgroundColor: 'var(--ps-colors-purple-500, #8b5cf6)',
    zIndex: 100,
    pointerEvents: 'none',
  };

  return (
    <>
      {shadowPortal}
      {showInsertionAbove && (
        <tr
          className="c-editor-table-tr__insertion-line-row"
          style={{ height: 0 }}
        >
          <td
            colSpan={100}
            style={{ padding: 0, position: 'relative', border: 'none' }}
          >
            <div style={{ ...insertionLineStyle, top: 0 }} />
          </td>
        </tr>
      )}
      <tr
        ref={combinedRef}
        test-id={`c-editor-table-tr-${rowIndex}`}
        data-rowid={rowId}
        data-row-index={rowIndex}
        data-selected={isRowSelected || undefined}
        className={classNames}
        key={rowId}
        // style={
        //   {
        //     '--dnd-actual-height': `${currentHeight} `,
        //   } as React.CSSProperties
        // }
        style={Object.keys(rowStyles).length > 0 ? rowStyles : undefined}
      >
        {renderIndexCell()}
        {childrenWithHandles}
      </tr>
      {showInsertionBelow && (
        <tr
          className="c-editor-table-tr__insertion-line-row"
          style={{ height: 0 }}
        >
          <td
            colSpan={100}
            style={{ padding: 0, position: 'relative', border: 'none' }}
          >
            <div style={{ ...insertionLineStyle, bottom: 0 }} />
          </td>
        </tr>
      )}
    </>
  );
};

const ForwardedDraggableRow = forwardRef(DraggableRowComponent);
export const DraggableRow = observer(ForwardedDraggableRow);
